import { useCrossSellingProductsStore } from '~/stores/productDetailPageStore/crossSellingProductsStore';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import {
  canDisplayProductFinderOnPdp,
  canLoadPdpCrosssellingAddons
} from '~/utils/shop/pdpCrosssellingAddonsEligibility';
import buildFeatureToggles from '~/utils/buildFeatureToggles';

const CROSSSELLING_SENTINEL_ROOT_MARGIN = '100px';

/**
 * Defers mounting the PDP cross-selling / recommended-products block until the
 * sentinel enters the viewport and the block is confirmed to have content.
 */
export function usePdpCrosssellingAddonsLazyLoad() {
  const { $globalPageSettings } = useNuxtApp();
  const logger = useLogger();
  const productStore = useProductDetailPageStore();
  const crossSellingStore = useCrossSellingProductsStore();

  const sentinelRef = ref<HTMLElement | null>(null);
  const hasObservedSentinel = ref(false);
  const loadedSku = ref<string | null>(null);
  const isPrefetching = ref(false);

  const displayProductFinder = computed(() => {
    const featureToggles = buildFeatureToggles($globalPageSettings.value);

    return canDisplayProductFinderOnPdp({
      showLensFinderOnPDP: featureToggles?.showLensFinderOnPDP,
      decoratedProduct: productStore.decoratedProduct
    });
  });

  const canLoadCrosssellingAddons = computed(() =>
    canLoadPdpCrosssellingAddons(
      productStore.decoratedProduct,
      productStore.loading
    )
  );

  // Recommended products of a sibling variant may stay on screen while the new
  // variant loads; data of an unrelated product must not.
  const itemsBelongToCurrentProduct = computed(() => {
    const loadedForUrlKey = crossSellingStore.recommendedProductsUrlKey;

    if (!loadedForUrlKey) {
      return false;
    }

    if (loadedForUrlKey === productStore.activeProduct?.url_key) {
      return true;
    }

    return (productStore.parentProduct?.variants ?? []).some(
      (variant) => variant?.product?.url_key === loadedForUrlKey
    );
  });

  // Derived instead of a flag: the PDP head is remounted per url_key, so a flag
  // would hide already loaded content until the observer fires again on every
  // variant switch.
  const showCrosssellingSection = computed(() => {
    if (hasObservedSentinel.value && displayProductFinder.value) {
      return true;
    }

    return (
      crossSellingStore.items.length > 0 && itemsBelongToCurrentProduct.value
    );
  });

  async function fetchRecommendedProducts(sku: string): Promise<void> {
    // Marked before awaiting so a failing request is not retried in a loop.
    loadedSku.value = sku;
    isPrefetching.value = true;
    try {
      await productStore.loadRecommendedProducts(sku);
    } catch (error) {
      logger.error('Failed to load recommended products for PDP block', error);
    } finally {
      isPrefetching.value = false;
    }
  }

  async function evaluateCrosssellingBlock(): Promise<void> {
    if (!canLoadCrosssellingAddons.value || isPrefetching.value) {
      return;
    }

    hasObservedSentinel.value = true;

    const sku = productStore.activeProduct?.sku;
    if (!sku || loadedSku.value === sku) {
      return;
    }

    await fetchRecommendedProducts(sku);
  }

  let observer: IntersectionObserver | null = null;

  function teardownIntersectionObserver(): void {
    observer?.disconnect();
    observer = null;
  }

  function setupIntersectionObserver(): void {
    if (!sentinelRef.value || observer || !canLoadCrosssellingAddons.value) {
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) {
          return;
        }

        void evaluateCrosssellingBlock();
      },
      { rootMargin: CROSSSELLING_SENTINEL_ROOT_MARGIN }
    );

    observer.observe(sentinelRef.value);
  }

  watch(
    () => sentinelRef.value,
    (element) => {
      teardownIntersectionObserver();
      if (element) {
        nextTick(() => {
          setupIntersectionObserver();
        });
      }
    },
    { immediate: true }
  );

  watch(canLoadCrosssellingAddons, (canLoad) => {
    if (!canLoad) {
      teardownIntersectionObserver();
      return;
    }

    nextTick(() => {
      setupIntersectionObserver();
    });
  });

  watch(
    () => productStore.urlKey,
    () => {
      teardownIntersectionObserver();
      nextTick(() => {
        setupIntersectionObserver();
      });
    }
  );

  // Refreshes the block for the new variant while the previous items stay
  // visible. A plain sku watcher is not enough: the head is remounted per
  // url_key, so the new instance often starts with the new sku already set.
  watchEffect(() => {
    const sku = productStore.activeProduct?.sku;

    if (
      !sku ||
      loadedSku.value === sku ||
      isPrefetching.value ||
      !canLoadCrosssellingAddons.value ||
      !showCrosssellingSection.value
    ) {
      return;
    }

    void fetchRecommendedProducts(sku);
  });

  onBeforeUnmount(() => {
    teardownIntersectionObserver();
  });

  return {
    sentinelRef,
    showCrosssellingSection,
    canLoadCrosssellingAddons
  };
}
